import { useCallback, useEffect, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Loader2, Inbox } from "lucide-react";
import api from "../lib/api";

export function useApi(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reload = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try {
      const r = await api.get(path);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);
  useEffect(() => { reload(); }, [reload]);
  return { data, loading, error, reload, setData };
}

export const PageHeader = ({ title, subtitle, actions, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6" data-testid={testid || "page-header"}>
    <div className="min-w-0">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A2E]">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

const BADGE = {
  green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  amber: "bg-amber-50 text-amber-700 ring-amber-200",
  red: "bg-rose-50 text-rose-700 ring-rose-200",
  violet: "bg-violet-50 text-violet-700 ring-violet-200",
  blue: "bg-sky-50 text-sky-700 ring-sky-200",
  gray: "bg-slate-100 text-slate-600 ring-slate-200",
  navy: "bg-[#1A1A2E] text-white ring-[#1A1A2E]",
};
const STATUS_TONE = {
  Paid: "green", Completed: "green", Delivered: "green", Accepted: "green", "Won / Project Confirmed": "green", "Final Approval": "green",
  "Partially Paid": "amber", "In Review": "amber", Review: "amber", Negotiation: "amber", "Follow-up": "amber", "Quotation Sent": "amber", Sent: "amber",
  Overdue: "red", Lost: "red", Canceled: "red", Cancelled: "red", Rejected: "red", "Revision Required": "red", Corrections: "red", Urgent: "red",
  Unpaid: "gray", "To Do": "gray", New: "gray", "New Lead": "gray", "On Hold": "gray", Low: "gray", Draft: "gray", Expired: "gray",
  "In Progress": "violet", Designing: "violet", Assigned: "blue", Contacted: "blue", "Requirements Collected": "blue", "Advance Received": "violet",
  High: "amber", Medium: "blue", owner: "navy", sales: "blue", designer: "violet",
  "Due Soon": "amber", "In Progress": "violet", "Corrections Required": "red", Approved: "blue", Active: "green", Probation: "blue",
  "On Notice": "amber", "On Leave": "amber", Resigned: "gray", Inactive: "gray", Outstanding: "amber", Adjusted: "green",
  "High Priority": "green", "Medium Priority": "blue", "Low Priority": "gray", "Needs Verification": "amber", "Newly Discovered": "violet",
  Verified: "green", "Needs Review": "amber", Stale: "gray", Invalid: "red", Duplicate: "red", "Awaiting approval": "amber",
  "Below threshold": "gray", Merged: "gray", success: "green", partial: "amber", failed: "red", running: "violet",
  "New prospect saved": "green", "Duplicate blocked": "red", Excluded: "gray", "Outside target regions": "gray",
  blocked: "red", "blocked updated": "amber", "flagged for review": "amber", merged: "violet", "kept separate": "blue",
};

export const StatusBadge = ({ value, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset whitespace-nowrap ${BADGE[STATUS_TONE[value] || "gray"]}`}>
    {value || "—"}
  </span>
);

const TONES = { revenue: "#7B2FFF", cash: "#10B981", expense: "#F43F5E", profit: "#4A00E0", pending: "#F59E0B", neutral: "#1A1A2E", loss: "#E11D48" };

export const KpiCard = ({ label, value, change, tone = "neutral", icon: Icon, hint, invert = false, testid }) => {
  const good = change == null ? null : invert ? change <= 0 : change >= 0;
  return (
    <div className="vmw-card p-4 relative overflow-hidden fade-up" data-testid={testid}>
      <div className="absolute left-0 top-0 h-full w-1" style={{ background: TONES[tone] }} />
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
        {Icon && <Icon className="h-4 w-4 shrink-0" style={{ color: TONES[tone] }} />}
      </div>
      <div className="font-num text-2xl font-bold mt-2 text-[#1A1A2E] truncate" data-testid={testid ? `${testid}-value` : undefined}>{value}</div>
      <div className="flex items-center gap-2 mt-1 min-h-[18px]">
        {change != null && (
          <span className={`inline-flex items-center text-xs font-semibold ${good ? "text-emerald-600" : "text-rose-600"}`}>
            {change >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {Math.abs(change)}%
          </span>
        )}
        {hint && <span className="text-[11px] text-muted-foreground truncate">{hint}</span>}
      </div>
    </div>
  );
};

export const Loading = ({ label = "Loading" }) => (
  <div className="flex items-center justify-center py-16 text-muted-foreground gap-2" data-testid="loading-state">
    <Loader2 className="h-5 w-5 animate-spin text-primary" /> {label}…
  </div>
);

export const Empty = ({ text = "Nothing here yet" }) => (
  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground text-sm gap-2" data-testid="empty-state">
    <Inbox className="h-8 w-8 text-violet-300" /> {text}
  </div>
);

export const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
    {children}
  </label>
);

export const Select = ({ options, value, onChange, placeholder, testid, className = "", ...rest }) => (
  <select data-testid={testid} className={`vmw-input pr-8 ${className}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...rest}>
    {placeholder != null && <option value="">{placeholder}</option>}
    {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
  </select>
);

export const Card = ({ title, actions, children, className = "", testid }) => (
  <div className={`vmw-card ${className}`} data-testid={testid}>
    {title && (
      <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-2">
        <h3 className="font-display font-semibold text-[15px] text-[#1A1A2E]">{title}</h3>
        {actions}
      </div>
    )}
    <div className="px-4 pb-4">{children}</div>
  </div>
);

export const RANGES = [
  { value: "today", label: "Today" }, { value: "yesterday", label: "Yesterday" }, { value: "last7", label: "Last 7 Days" },
  { value: "last15", label: "Last 15 Days" }, { value: "this_month", label: "This Month" }, { value: "prev_month", label: "Previous Month" },
  { value: "fy", label: "Current Financial Year" }, { value: "custom", label: "Custom Range" },
];

export const rangeQuery = (r) => `range=${r.range}${r.range === "custom" && r.start && r.end ? `&start=${r.start}&end=${r.end}` : ""}`;

export const RangePicker = ({ value, onChange }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="date-range-picker">
    <Select testid="date-range-select" className="w-48" options={RANGES} value={value.range} onChange={(v) => onChange({ ...value, range: v })} />
    {value.range === "custom" && (
      <>
        <input type="date" data-testid="date-range-start" className="vmw-input w-40" value={value.start || ""} onChange={(e) => onChange({ ...value, start: e.target.value })} />
        <input type="date" data-testid="date-range-end" className="vmw-input w-40" value={value.end || ""} onChange={(e) => onChange({ ...value, end: e.target.value })} />
      </>
    )}
  </div>
);

export const Pager = ({ page, total, pageSize, onPage }) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between px-3 py-3 border-t text-sm text-muted-foreground" data-testid="pager">
      <span>{total} records</span>
      <div className="flex items-center gap-2">
        <button data-testid="pager-prev" disabled={page <= 1} onClick={() => onPage(page - 1)} className="px-2.5 py-1 rounded-md border disabled:opacity-40 hover:bg-muted">Prev</button>
        <span>{page} / {pages}</span>
        <button data-testid="pager-next" disabled={page >= pages} onClick={() => onPage(page + 1)} className="px-2.5 py-1 rounded-md border disabled:opacity-40 hover:bg-muted">Next</button>
      </div>
    </div>
  );
};

export const TableWrap = ({ children }) => <div className="vmw-card overflow-x-auto">{children}</div>;
